import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Flex, Text, Stack, Radio, RadioGroup, RangeSlider, RangeSliderTrack, RangeSliderFilledTrack, RangeSliderThumb, Box, Icon, } from "@chakra-ui/react";
import { MdCheck } from "react-icons/md";
import FilterDrawer from "../common/FilterDrawer";
const getFilterCategories = (tables) => {
    const baseCategories = [
        { key: "availability", label: "Availability" },
        { key: "area", label: "Area" },
        { key: "tableType", label: "Table Type" },
        { key: "tableFor", label: "Table For" },
        { key: "sortBy", label: "Sort By" },
    ];
    // Only add price range filter if there are tables with non-zero prices
    const hasNonZeroPrices = tables.some((table) => (table.price || 0) > 0);
    if (hasNonZeroPrices) {
        baseCategories.splice(4, 0, { key: "priceRange", label: "Price Range" });
    }
    return baseCategories;
};
const AVAILABILITY_OPTIONS = [
    { label: "Fast Filling", value: "fast_filling" },
    { label: "Sold Out", value: "sold_out" },
];
const SORT_OPTIONS = [
    { value: "price_low", label: "Price: Low To High" },
    { value: "price_high", label: "Price: High To Low" },
];
const TableFilterDrawer = ({ isOpen, onClose, tables, areas, tableTypes, seatCounts, initialFilters, onApply, onClearAll, }) => {
    // Local filter state
    const [availability, setAvailability] = useState(initialFilters.availability || []);
    const [area, setArea] = useState(initialFilters.area || []);
    const [tableType, setTableType] = useState(initialFilters.tableType || []);
    const [tableFor, setTableFor] = useState(initialFilters.tableFor || []);
    const [sortBy, setSortBy] = useState(initialFilters.sortBy || "");
    const [priceRange, setPriceRange] = useState(initialFilters.priceRange || [0, 100000]);
    useEffect(() => {
        setAvailability(initialFilters.availability || []);
        setArea(initialFilters.area || []);
        setTableType(initialFilters.tableType || []);
        setTableFor(initialFilters.tableFor || []);
        setSortBy(initialFilters.sortBy || "");
        setPriceRange(initialFilters.priceRange || [0, 100000]);
    }, [initialFilters]);
    // Compute min/max price from tables
    const minPrice = tables.length
        ? Math.min(...tables.map((table) => table.price || 0))
        : 0;
    const maxPrice = tables.length
        ? Math.max(...tables.map((table) => table.price || 0))
        : 100000;
    // Count options for each filter
    const getActiveCount = (categoryKey) => {
        switch (categoryKey) {
            case "availability":
                return availability.length;
            case "area":
                return area.length;
            case "tableType":
                return tableType.length;
            case "tableFor":
                return tableFor.length;
            case "sortBy":
                return sortBy !== "" ? 1 : 0;
            case "priceRange":
                return priceRange[0] !== minPrice || priceRange[1] !== maxPrice ? 1 : 0;
            default:
                return 0;
        }
    };
    // Count tables by availability
    const getAvailabilityCounts = (tables) => {
        const counts = {
            fast_filling: 0,
            sold_out: 0,
        };
        tables.forEach((table) => {
            if (table.availableTables > 0) {
                counts.fast_filling++;
            }
            else {
                counts.sold_out++;
            }
        });
        return counts;
    };
    // Count tables by area
    const getAreaCounts = (tables) => {
        const counts = {};
        areas.forEach((area) => {
            counts[area] = tables.filter((table) => table.areaName === area).length;
        });
        return counts;
    };
    // Count tables by type
    const getTableTypeCounts = (tables) => {
        const counts = {};
        tableTypes.forEach((type) => {
            counts[type] = tables.filter((table) => table.tableType === type).length;
        });
        return counts;
    };
    // Count tables by seat count
    const getSeatCounts = (tables) => {
        const counts = {};
        seatCounts.forEach((seats) => {
            counts[seats.toString()] = tables.filter((table) => table.seatCount === seats).length;
        });
        return counts;
    };
    // Render right panel options
    const renderOptions = (categoryKey) => {
        switch (categoryKey) {
            case "availability":
                const availabilityCounts = getAvailabilityCounts(tables);
                return (_jsx(Stack, { spacing: 4, children: AVAILABILITY_OPTIONS.map((option) => {
                        const isSelected = availability.includes(option.value);
                        return (_jsxs(Flex, { align: "center", justify: "space-between", cursor: "pointer", py: 1, borderRadius: "md", fontSize: "xs", fontWeight: 400, onClick: () => {
                                setAvailability((prev) => isSelected
                                    ? prev.filter((v) => v !== option.value)
                                    : [...prev, option.value]);
                            }, children: [_jsxs(Flex, { align: "center", gap: 3, children: [isSelected && (_jsx(Icon, { as: MdCheck, boxSize: 5, color: "brand" })), _jsx(Text, { color: "white", children: option.label })] }), _jsx(Text, { color: "white", children: availabilityCounts[option.value] })] }, option.value));
                    }) }));
            case "area":
                const areaCounts = getAreaCounts(tables);
                return (_jsx(Stack, { spacing: 4, children: areas.map((areaName) => {
                        const isSelected = area.includes(areaName);
                        return (_jsxs(Flex, { align: "center", justify: "space-between", cursor: "pointer", py: 1, borderRadius: "md", fontSize: "xs", fontWeight: 400, onClick: () => {
                                setArea((prev) => isSelected
                                    ? prev.filter((v) => v !== areaName)
                                    : [...prev, areaName]);
                            }, children: [_jsxs(Flex, { align: "center", gap: 3, children: [isSelected && (_jsx(Icon, { as: MdCheck, boxSize: 5, color: "brand" })), _jsx(Text, { color: "white", children: areaName })] }), _jsx(Text, { color: "white", children: areaCounts[areaName] || 0 })] }, areaName));
                    }) }));
            case "tableType":
                const tableTypeCounts = getTableTypeCounts(tables);
                return (_jsx(Stack, { spacing: 4, children: tableTypes.map((type) => {
                        const isSelected = tableType.includes(type);
                        return (_jsxs(Flex, { align: "center", justify: "space-between", cursor: "pointer", py: 1, borderRadius: "md", fontSize: "xs", fontWeight: 400, onClick: () => {
                                setTableType((prev) => isSelected
                                    ? prev.filter((v) => v !== type)
                                    : [...prev, type]);
                            }, children: [_jsxs(Flex, { align: "center", gap: 3, children: [isSelected && (_jsx(Icon, { as: MdCheck, boxSize: 5, color: "brand" })), _jsx(Text, { color: "white", children: type })] }), _jsx(Text, { color: "white", children: tableTypeCounts[type] || 0 })] }, type));
                    }) }));
            case "tableFor":
                const seatCountsData = getSeatCounts(tables);
                return (_jsx(Stack, { spacing: 4, children: seatCounts.map((seats) => {
                        const isSelected = tableFor.includes(seats.toString());
                        return (_jsxs(Flex, { align: "center", justify: "space-between", cursor: "pointer", py: 1, borderRadius: "md", fontSize: "xs", fontWeight: 400, onClick: () => {
                                setTableFor((prev) => isSelected
                                    ? prev.filter((v) => v !== seats.toString())
                                    : [...prev, seats.toString()]);
                            }, children: [_jsxs(Flex, { align: "center", gap: 3, children: [isSelected && (_jsx(Icon, { as: MdCheck, boxSize: 5, color: "brand" })), _jsxs(Text, { color: "white", children: [seats, " Seats"] })] }), _jsx(Text, { color: "white", children: seatCountsData[seats.toString()] || 0 })] }, seats));
                    }) }));
            case "sortBy":
                return (_jsx(RadioGroup, { value: sortBy, onChange: (val) => setSortBy(val), children: _jsx(Stack, { spacing: 6, children: SORT_OPTIONS.map((opt) => (_jsx(Radio, { value: opt.value, colorScheme: "purple", children: _jsx(Text, { fontSize: "xs", children: opt.label }) }, opt.value))) }) }));
            case "priceRange":
                return (_jsxs(Box, { w: "100%", children: [_jsx(Text, { fontSize: "xs", fontWeight: "400", mb: 4, children: "Select Price Range" }), _jsxs(Text, { fontSize: "md", fontWeight: "600", mr: 2, mb: 4, children: ["\u20B9", priceRange[0], " - \u20B9", priceRange[1]] }), _jsxs(RangeSlider, { min: 0, max: maxPrice, step: 100, defaultValue: priceRange, value: priceRange, onChange: (val) => setPriceRange(val), minStepsBetweenThumbs: 1, flex: 1, colorScheme: "purple", "aria-label": ["min", "max"], children: [_jsx(RangeSliderTrack, { children: _jsx(RangeSliderFilledTrack, {}) }), _jsx(RangeSliderThumb, { index: 0 }), _jsx(RangeSliderThumb, { index: 1 })] })] }));
            default:
                return null;
        }
    };
    const handleApply = () => {
        onApply({
            availability,
            area,
            tableType,
            tableFor,
            priceRange,
            sortBy,
        });
    };
    return (_jsx(FilterDrawer, { isOpen: isOpen, onClose: onClose, categories: getFilterCategories(tables), initialFilters: {
            availability,
            area,
            tableType,
            tableFor,
            priceRange,
            sortBy,
        }, onApply: handleApply, onClearAll: onClearAll, renderOptions: renderOptions, getActiveCount: getActiveCount }));
};
export default TableFilterDrawer;
